import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  InputAdornment,
  Grid
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import BalanceIcon from '@mui/icons-material/Balance';
import CancelIcon from '@mui/icons-material/Cancel';
import UndoIcon from '@mui/icons-material/Undo';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import disputeAdminService from '../../services/disputeAdminService';
import currencyConversionService from '../../services/currencyConversionService';

const DECISION_OPTIONS = [
  { value: 'favor_owner', label: 'En faveur du propriétaire', icon: <HomeIcon />, color: 'success' },
  { value: 'favor_renter', label: 'En faveur du locataire', icon: <PersonIcon />, color: 'info' },
  { value: 'shared', label: 'Partage de responsabilité', icon: <BalanceIcon />, color: 'warning' },
  { value: 'cancel_reservation', label: 'Annulation de la réservation', icon: <CancelIcon />, color: 'secondary' },
  { value: 'dismiss', label: 'Rejeter le litige', icon: <CancelIcon />, color: 'error' },
  { value: 'return_to_parties', label: 'Renvoyer aux parties', icon: <UndoIcon />, color: 'default', disabled: true }
];

const AdminDecisionPanel = ({ dispute, onDecisionMade, disabled = false, financialSummary = null, ownerInfo = null, renterInfo = null }) => {
  const { t } = useTranslation('disputes');
  const { enqueueSnackbar } = useSnackbar();
  const [decision, setDecision] = useState('');
  const [message, setMessage] = useState('');
  const [amount, setAmount] = useState('');
  const [ownerAmount, setOwnerAmount] = useState('');
  const [renterAmount, setRenterAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const rentalTotal = financialSummary?.rentalTotal ?? null;
  const depositAmountFS = financialSummary?.depositAmount ?? null;

  // 🔄 Helper: Valider le montant minimum pour compensation Stripe
  const validateStripeCompensationAmount = async (compensationAmount, compensationType) => {
    const paymentMethod = dispute?.reservation?.payments?.[0]?.payment_method?.toLowerCase();
    const isStripPayment = paymentMethod === 'stripe';
    const isOwnerCompensation = compensationType === 'owner_compensation';

    console.log('[AdminDecisionPanel] 📋 Pre-validation check:', {
      compensationType,
      isOwnerCompensation,
      paymentMethod,
      isStripPayment,
      amount: compensationAmount
    });

    // Si c'est une compensation au propriétaire via Stripe, vérifier le montant minimum
    if (isOwnerCompensation && isStripPayment && compensationAmount > 0) {
      const reservationCurrency = dispute?.reservation?.currency || 'EUR';
      
      console.log('[AdminDecisionPanel] 💱 Checking Stripe minimum ($10 USD):', {
        amount: compensationAmount,
        currency: reservationCurrency
      });

      const conversionResult = await currencyConversionService.convertToUSD(compensationAmount, reservationCurrency);
      
      console.log('[AdminDecisionPanel] 🔄 Conversion result:', conversionResult);

      if (!conversionResult.success) {
        const errorMsg = conversionResult.error || 'Erreur lors de la vérification du montant';
        console.log('[AdminDecisionPanel] ❌ Conversion failed:', errorMsg);
        throw new Error(errorMsg);
      }

      if (!conversionResult.meetsMinimum) {
         // Montant trop petit
        const minAmountMsg = t(
          'amicable.stripMinimumError',
          `⚠️ Montant minimum: Le paiement par Stripe requiert un montant minimum de 10 USD (environ 0,50€). Vous avez proposé ${conversionResult.equivalentInUSD} USD. Veuillez augmenter le montant ou choisir une autre type de compensation (annulation par exemple).`
        );
        console.log('[AdminDecisionPanel] ❌ Amount too small:', conversionResult);
        throw new Error(minAmountMsg);
      }

      console.log('[AdminDecisionPanel] ✅ Amount validation passed:', {
        amountInUSD: conversionResult.amountInUSD,
        meetsMinimum: true
      });
    }

    return true;
  };

  const validation = (() => {
    const errors = {};

    const toNumber = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : null;
    };

    if (decision === 'favor_owner') {
      const n = toNumber(amount);
      if (n === null || n <= 0) errors.amount = 'Le montant est requis et doit être positif';
      if (depositAmountFS != null && n != null && n > depositAmountFS) errors.amount = 'Ne doit pas dépasser la caution';
    }

    if (decision === 'favor_renter') {
      const n = toNumber(amount);
      if (n === null || n <= 0) errors.amount = 'Le montant est requis et doit être positif';
      if (rentalTotal != null && n != null && n > rentalTotal) errors.amount = 'Ne doit pas dépasser le montant de location';
    }

    if (decision === 'shared') {
      const ownerN = toNumber(ownerAmount);
      const renterN = toNumber(renterAmount);
      if (ownerN === null || ownerN <= 0) errors.ownerAmount = 'Montant requis et positif';
      if (renterN === null || renterN <= 0) errors.renterAmount = 'Montant requis et positif';

      if (ownerN != null && renterN != null) {
        const diff = Math.abs(ownerN - renterN);
        if (ownerN > renterN && depositAmountFS != null && diff > depositAmountFS) {
          errors.ownerAmount = 'Différentiel > caution';
        }
        if (renterN > ownerN && rentalTotal != null && diff > rentalTotal) {
          errors.renterAmount = 'Différentiel > montant location';
        }
      }
    }

    const isValid = Object.keys(errors).length === 0;
    return { isValid, errors };
  })();

  const handleSubmit = async () => {
    if (!decision) {
      setError('Veuillez sélectionner une décision');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 🔄 Valider Stripe si compensation au propriétaire
      if (decision === 'favor_owner' && amount) {
        try {
          await validateStripeCompensationAmount(parseFloat(amount), 'owner_compensation');
        } catch (validationError) {
          setError(validationError.message);
          enqueueSnackbar(validationError.message, { variant: 'error' });
          setLoading(false);
          return;
        }
      }

      const payload = {
        decision,
        message: message || undefined
      };

      // Ajouter le montant selon le type de décision
      if (decision === 'favor_owner' || decision === 'favor_renter') {
        if (amount) {
          payload.amount = parseFloat(amount);
        }
      } else if (decision === 'shared') {
        if (ownerAmount) payload.ownerAmount = parseFloat(ownerAmount);
        if (renterAmount) payload.renterAmount = parseFloat(renterAmount);
      } else if (decision === 'cancel_reservation') {
        // Pas de montant pour une annulation pure
      }

      const result = await disputeAdminService.adminDecision(dispute.dispute_id || dispute.id, payload);
      onDecisionMade?.(result);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erreur lors de l\'enregistrement de la décision');
    } finally {
      setLoading(false);
    }
  };

  // Ne pas afficher si le litige n'est pas escaladé
  if (dispute.status !== 'escalated') {
    return null;
  }

  const selectedOption = DECISION_OPTIONS.find(opt => opt.value === decision);

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 2, bgcolor: 'background.paper', border: '2px solid', borderColor: 'primary.main' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <GavelIcon color="primary" />
        <Typography variant="h6" fontWeight="bold">
          {t('disputes.admin.decisionTitle', 'Décision administrative')}
        </Typography>
        <Chip label="Admin" size="small" color="primary" />
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('disputes.admin.decisionDescription', 'En tant qu\'administrateur, vous pouvez prendre une décision finale sur ce litige escaladé.')}
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <RadioGroup
        value={decision}
        onChange={(e) => setDecision(e.target.value)}
      >
        <Stack spacing={1}>
          {DECISION_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              disabled={loading || disabled || option.disabled}
              control={<Radio color={option.color} />}
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  {option.icon}
                  <Typography>{t(`disputes.admin.decision.${option.value}`, option.label)}</Typography>
                </Stack>
              }
              sx={{
                border: '1px solid',
                borderColor: decision === option.value ? `${option.color}.main` : 'divider',
                borderRadius: 1,
                p: 1,
                m: 0,
                bgcolor: decision === option.value ? `${option.color}.50` : 'transparent',
                '&:hover': {
                  bgcolor: `${option.color}.50`
                }
              }}
            />
          ))}
        </Stack>
      </RadioGroup>

      {decision === 'cancel_reservation' && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t(
            'disputes.admin.cancelReservationImpact',
            'En validant, la réservation sera annulée : le locataire sera remboursé intégralement du montant de la réservation et sa caution sera libérée.'
          )}
        </Alert>
      )}

      {/* Champs conditionnels selon la décision */}
      {(decision === 'favor_owner' || decision === 'favor_renter') && (
        <Box sx={{ mt: 2 }}>
          <TextField
            label={t('disputes.admin.compensationAmount', 'Montant de compensation (optionnel)')}
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            fullWidth
            size="small"
            disabled={loading || disabled}
            InputProps={{
              startAdornment: <InputAdornment position="start">€</InputAdornment>
            }}
            error={Boolean(validation.errors.amount)}
            helperText={
              validation.errors.amount || (
                decision === 'favor_owner'
                  ? t('disputes.admin.ownerCompensationHelp', 'Montant à verser au propriétaire')
                  : t('disputes.admin.renterRefundHelp', 'Montant à rembourser au locataire')
              )
            }
          />
        </Box>
      )}

      {decision === 'shared' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <TextField
            label={t('disputes.admin.ownerAmount', 'Montant propriétaire')}
            type="number"
            value={ownerAmount}
            onChange={(e) => setOwnerAmount(e.target.value)}
            fullWidth
            size="small"
            disabled={loading || disabled}
            InputProps={{
              startAdornment: <InputAdornment position="start">€</InputAdornment>
            }}
            error={Boolean(validation.errors.ownerAmount)}
            helperText={validation.errors.ownerAmount || ''}
          />
          <TextField
            label={t('disputes.admin.renterAmount', 'Montant locataire')}
            type="number"
            value={renterAmount}
            onChange={(e) => setRenterAmount(e.target.value)}
            fullWidth
            size="small"
            disabled={loading || disabled}
            InputProps={{
              startAdornment: <InputAdornment position="start">€</InputAdornment>
            }}
            error={Boolean(validation.errors.renterAmount)}
            helperText={validation.errors.renterAmount || ''}
          />
        </Stack>
      )}

      {/* Infos utilisateur - Owner et Renter */}
      {(ownerInfo || renterInfo) && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Infos Propriétaire */}
          {ownerInfo && (
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, bgcolor: 'primary.lighter', borderLeft: 4, borderColor: 'primary.main' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  🏠 Propriétaire
                </Typography>
                <Typography variant="body2">
                  <strong>Nom :</strong> {ownerInfo.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Email :</strong> {ownerInfo.email}
                </Typography>
                <Typography variant="body2">
                  <strong>Langue :</strong> {ownerInfo.language || 'en'}
                </Typography>
              </Paper>
            </Grid>
          )}

          {/* Infos Locataire */}
          {renterInfo && (
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, bgcolor: 'info.lighter', borderLeft: 4, borderColor: 'info.main' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  🔑 Locataire
                </Typography>
                <Typography variant="body2">
                  <strong>Nom :</strong> {renterInfo.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Email :</strong> {renterInfo.email}
                </Typography>
                <Typography variant="body2">
                  <strong>Langue :</strong> {renterInfo.language || 'en'}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Message optionnel */}
      <TextField
        label={t('disputes.admin.decisionMessage', 'Message explicatif (optionnel)')}
        multiline
        rows={3}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        fullWidth
        sx={{ mt: 3 }}
        disabled={loading || disabled}
        placeholder={t('disputes.admin.decisionMessagePlaceholder', 'Expliquez votre décision aux parties concernées...')}
      />

      {financialSummary && (
        <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                Montant net
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: financialSummary.currency || 'EUR' }).format(financialSummary.netAmount || 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {financialSummary.isReal ? 'Données réelles' : 'Données partielles'}
              </Typography>
            </Box>
            <Box textAlign="right">
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                Caution
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.dark' }}>
                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: financialSummary.currency || 'EUR' }).format(financialSummary.depositAmount || 0)}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button
          variant="contained"
          color={selectedOption?.color || 'primary'}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
          onClick={handleSubmit}
          disabled={loading || disabled || !decision || !validation.isValid}
          sx={{ minWidth: 200 }}
        >
          {loading 
            ? t('disputes.admin.processing', 'Traitement...')
            : t('disputes.admin.confirmDecision', 'Confirmer la décision')
          }
        </Button>
      </Stack>
    </Paper>
  );
};

export default AdminDecisionPanel;